import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API_URL = 'https://iia-backend-production.up.railway.app';

const CourseView = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [course, setCourse] = useState(null);
  const [units, setUnits] = useState([]);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const [courseRes, unitsRes] = await Promise.all([
        axios.get(`${API_URL}/api/instructional/courses/${courseId}`),
        axios.get(`${API_URL}/api/instructional/courses/${courseId}/units`)
      ]);
      setCourse(courseRes.data.course);
      setUnits(unitsRes.data.units);

      // Fetch modules for each unit
      const modulesData = {};
      for (const unit of unitsRes.data.units) {
        const modRes = await axios.get(
          `${API_URL}/api/instructional/units/${unit.id}/modules`
        );
        modulesData[unit.id] = modRes.data.modules;
      }
      setModules(modulesData);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Impact Influence Academy</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcome}>Welcome, {user?.first_name}</span>
          <button
            style={styles.returnBtn}
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
        </div>
      </div>

      <div style={styles.main}>
        {loading && (
          <div style={styles.centered}>
            <p style={styles.loadingText}>Loading course...</p>
          </div>
        )}

        {!loading && course && (
          <>
            {/* Course Header */}
            <div style={styles.courseHeader}>
              <h2 style={styles.pageTitle}>📚 {course.title}</h2>
              <p style={styles.pageSubtitle}>{course.description}</p>
            </div>

            {/* Units and Modules */}
            <div style={styles.unitsContainer}>
              {units.map((unit, unitIndex) => (
                <div key={unit.id} style={styles.unitCard}>
                  {/* Unit Header */}
                  <div style={styles.unitHeader}>
                    <span style={styles.unitBadge}>
                      Unit {unitIndex + 1}
                    </span>
                    <h3 style={styles.unitTitle}>{unit.title}</h3>
                    {unit.description && (
                      <p style={styles.unitDesc}>{unit.description}</p>
                    )}
                  </div>

                  {/* Modules */}
                  <div style={styles.modulesContainer}>
                    {(modules[unit.id] || []).length === 0 && (
                      <p style={styles.emptyText}>
                        No modules in this unit yet
                      </p>
                    )}
                    {(modules[unit.id] || []).map((mod, modIndex) => (
                      <div
                        key={mod.id}
                        style={mod.is_locked ? 
                          styles.moduleCardLocked : styles.moduleCard}
                        onClick={() => {
                          if (!mod.is_locked) {
                            navigate(`/instructional/module/${mod.id}`);
                          }
                        }}
                      >
                        <div style={styles.moduleLeft}>
                          <span style={mod.is_locked ? 
                            styles.moduleBadgeLocked : styles.moduleBadge}>
                            Week {modIndex + 1}
                          </span>
                          <div>
                            <h4 style={mod.is_locked ? 
                              styles.moduleTitleLocked : styles.moduleTitle}>
                              {mod.title}
                            </h4>
                            {mod.is_locked && mod.lock_reasons?.length > 0 && (
                              <div style={styles.lockReasons}>
                                {mod.lock_reasons.map((reason, i) => (
                                  <span key={i} style={styles.lockReason}>
                                    🔒 {reason}
                                  </span>
                                ))}
                              </div>
                            )}
                            {!mod.is_locked && mod.description && (
                              <p style={styles.moduleDesc}>
                                {mod.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div style={styles.moduleRight}>
                          {mod.is_locked ? (
                            <span style={styles.lockedIcon}>🔒</span>
                          ) : (
                            <>
                              <span style={styles.dripText}>
                                🕐 Day {mod.drip_days}
                              </span>
                              <button style={styles.enterBtn}>
                                Enter →
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1A2744',
    color: '#ffffff',
  },
  header: {
    backgroundColor: '#0f1a33',
    borderBottom: '1px solid #C49A28',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    color: '#C49A28',
    fontSize: '24px',
    fontWeight: '800',
    margin: '0',
    letterSpacing: '2px',
  },
  logoText: {
    color: '#ffffff80',
    fontSize: '14px',
    letterSpacing: '1px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  welcome: {
    color: '#ffffff',
    fontSize: '14px',
  },
  returnBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #C49A28',
    color: '#C49A28',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  main: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '32px',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    padding: '60px',
  },
  loadingText: {
    color: '#ffffff60',
    fontSize: '16px',
  },
  courseHeader: {
    marginBottom: '32px',
  },
  pageTitle: {
    color: '#C49A28',
    fontSize: '28px',
    fontWeight: '700',
    margin: '0 0 8px 0',
  },
  pageSubtitle: {
    color: '#ffffff60',
    fontSize: '14px',
    margin: '0',
    lineHeight: '1.6',
  },
  unitsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  unitCard: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  unitHeader: {
    padding: '20px 24px',
    borderBottom: '1px solid #ffffff10',
    backgroundColor: '#0d1628',
  },
  unitBadge: {
    backgroundColor: '#C49A2820',
    color: '#C49A28',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '1px',
    display: 'inline-block',
    marginBottom: '8px',
  },
  unitTitle: {
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: '600',
    margin: '0 0 4px 0',
  },
  unitDesc: {
    color: '#ffffff60',
    fontSize: '13px',
    margin: '0',
  },
  modulesContainer: {
    padding: '16px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  emptyText: {
    color: '#ffffff30',
    fontSize: '14px',
  },
  moduleCard: {
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff10',
    borderRadius: '8px',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  moduleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  moduleBadge: {
    backgroundColor: '#1A6B8A20',
    color: '#1A6B8A',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  moduleTitle: {
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '600',
    margin: '0 0 2px 0',
  },
  moduleDesc: {
    color: '#ffffff60',
    fontSize: '13px',
    margin: '0',
  },
  moduleRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  dripText: {
    color: '#ffffff40',
    fontSize: '12px',
  },
  enterBtn: {
    backgroundColor: '#C49A28',
    color: '#1A2744',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  moduleCardLocked: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff08',
    borderRadius: '8px',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  moduleBadgeLocked: {
    backgroundColor: '#ffffff10',
    color: '#ffffff30',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  moduleTitleLocked: {
    color: '#ffffff50',
    fontSize: '15px',
    fontWeight: '600',
    margin: '0 0 4px 0',
  },
  lockReasons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginTop: '4px',
  },
  lockReason: {
    color: '#ffffff30',
    fontSize: '11px',
  },
  lockedIcon: {
    fontSize: '18px',
    opacity: 0.4,
  },
};

export default CourseView;